import {CategoryId, CATEGORY_IDS} from '@mira/api';

import {isNil} from 'ramda';

import {CATEGORY_MAP, CategoryDefinition} from '../constants/categoryMapping';

const CATEGORY_ID_SET = new Set<string>(CATEGORY_IDS);

function isCategoryId(key: string): key is CategoryId {
  return CATEGORY_ID_SET.has(key);
}

function warnUnknownCategoryId(categoryId: string): void {
  if (process.env.NODE_ENV !== 'production') {
    globalThis.console.warn(`[mira] unknown categoryId from backend: ${categoryId}`);
  }
}

function warnMissingCategoryMapping(categoryId: string): void {
  if (process.env.NODE_ENV !== 'production') {
    globalThis.console.warn(`[mira] known categoryId missing from CATEGORY_MAP: ${categoryId}`);
  }
}

/**
 * Strict category lookup.
 *
 * Returns `null` for unknown category ids and logs a one-line warning in
 * non-production builds so backend drift is surfaced as a missing row in the
 * report (and a console warning for engineers) instead of being papered over
 * with a misleading "Inspection and repair" duplicate. Also warns when a
 * known `CategoryId` is missing from `CATEGORY_MAP` so frontend mapping drift
 * is not hidden behind a silent `null`.
 *
 * Callers (`transformCategory`, `useTransformedPredictions`) filter the `null`
 * before rendering.
 */
export function getCategory(categoryId: string): CategoryDefinition | null {
  if (isCategoryId(categoryId)) {
    const category = CATEGORY_MAP.get(categoryId);
    if (isNil(category)) {
      warnMissingCategoryMapping(categoryId);
      return null;
    }
    return category;
  }
  warnUnknownCategoryId(categoryId);

  return null;
}
